import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    CircularProgress,
    LinearProgress,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    IconButton,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import DeleteIcon from '@mui/icons-material/Delete';
import { uploadDocument, formatFileSize } from '../services/closeoutDocsService';

/**
 * UploadDialog Component
 * Dialog for uploading documents with drag-and-drop functionality
 *
 * @param {boolean} open - Whether the dialog is open
 * @param {Function} onClose - Callback to close the dialog
 * @param {Function} onSuccess - Callback called on successful upload
 * @param {string} projectId - The UUID of the project
 * @param {string|null} folderId - The UUID of the folder (null for root)
 */
const UploadDialog = ({ open, onClose, onSuccess, projectId, folderId }) => {
    const theme = useTheme();
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [dragActive, setDragActive] = useState(false);
    const [error, setError] = useState('');

    const handleClose = () => {
        if (!uploading) {
            setSelectedFiles([]);
            setError('');
            setUploadProgress(0);
            onClose();
        }
    };

    const handleDrag = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === 'dragenter' || e.type === 'dragover') {
            setDragActive(true);
        } else if (e.type === 'dragleave') {
            setDragActive(false);
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);

        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            handleFiles(Array.from(e.dataTransfer.files));
        }
    };

    const handleFileSelect = (e) => {
        if (e.target.files && e.target.files.length > 0) {
            handleFiles(Array.from(e.target.files));
        }
    };

    const handleFiles = (files) => {
        // Add new files to existing selection
        setSelectedFiles((prev) => [...prev, ...files]);
        setError('');
    };

    const handleRemoveFile = (index) => {
        setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
    };

    const handleUpload = async () => {
        if (selectedFiles.length === 0) {
            setError('Please select at least one file');
            return;
        }

        setUploading(true);
        setError('');
        setUploadProgress(0);

        try {
            // Upload files sequentially
            for (let i = 0; i < selectedFiles.length; i++) {
                const file = selectedFiles[i];
                await uploadDocument(projectId, file, folderId);
                setUploadProgress(((i + 1) / selectedFiles.length) * 100);
            }

            // Success
            setSelectedFiles([]);
            setUploadProgress(0);
            if (onSuccess) {
                onSuccess();
            }
        } catch (err) {
            console.error('Error uploading files:', err);
            setError(err.response?.data?.message || 'Failed to upload files');
        } finally {
            setUploading(false);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 2,
                },
            }}
        >
            <DialogTitle sx={{ fontWeight: 600 }}>Upload Documents</DialogTitle>
            <DialogContent>
                <Box sx={{ pt: 1 }}>
                    {/* Drag and Drop Area */}
                    <Box
                        component="label"
                        htmlFor="closeout-document-input"
                        onDragEnter={handleDrag}
                        onDragLeave={handleDrag}
                        onDragOver={handleDrag}
                        onDrop={handleDrop}
                        sx={{
                            border: `2px dashed ${
                                dragActive ? theme.palette.primary.main : theme.palette.divider
                            }`,
                            borderRadius: 2,
                            p: 4,
                            textAlign: 'center',
                            cursor: uploading ? 'not-allowed' : 'pointer',
                            backgroundColor: dragActive
                                ? theme.palette.action.hover
                                : theme.palette.background.default,
                            transition: 'all 0.2s ease',
                            '&:hover': {
                                backgroundColor: !uploading && theme.palette.action.hover,
                                borderColor: !uploading && theme.palette.primary.main,
                            },
                            '&:focus-within': {
                                outline: '2px solid #1652F0',
                                outlineOffset: '2px',
                            },
                        }}
                    >
                        <CloudUploadIcon
                            sx={{
                                fontSize: 48,
                                color: dragActive
                                    ? theme.palette.primary.main
                                    : theme.palette.grey[400],
                                mb: 2,
                            }}
                        />
                        <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>
                            {dragActive
                                ? 'Drop files here'
                                : 'Drag and drop files here, or click to select'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            Multiple files supported
                        </Typography>
                        <input
                            id="closeout-document-input"
                            type="file"
                            aria-label="Choose closeout documents"
                            multiple
                            onChange={handleFileSelect}
                            disabled={uploading}
                            style={{
                                position: 'absolute',
                                width: 1,
                                height: 1,
                                padding: 0,
                                margin: -1,
                                overflow: 'hidden',
                                clip: 'rect(0 0 0 0)',
                                whiteSpace: 'nowrap',
                                border: 0,
                            }}
                        />
                    </Box>

                    {/* Selected Files List */}
                    {selectedFiles.length > 0 && (
                        <Box sx={{ mt: 3 }}>
                            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                                Selected Files ({selectedFiles.length})
                            </Typography>
                            <List dense>
                                {selectedFiles.map((file, index) => (
                                    <ListItem
                                        key={index}
                                        secondaryAction={
                                            !uploading && (
                                                <IconButton
                                                    edge="end"
                                                    size="small"
                                                    onClick={() => handleRemoveFile(index)}
                                                >
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            )
                                        }
                                        sx={{
                                            border: `1px solid ${theme.palette.divider}`,
                                            borderRadius: 1,
                                            mb: 1,
                                        }}
                                    >
                                        <ListItemIcon>
                                            <InsertDriveFileIcon />
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={file.name}
                                            secondary={formatFileSize(file.size)}
                                        />
                                    </ListItem>
                                ))}
                            </List>
                        </Box>
                    )}

                    {/* Upload Progress */}
                    {uploading && (
                        <Box sx={{ mt: 2 }}>
                            <LinearProgress variant="determinate" value={uploadProgress} />
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ mt: 0.5, display: 'block', textAlign: 'center' }}
                            >
                                Uploading... {Math.round(uploadProgress)}%
                            </Typography>
                        </Box>
                    )}

                    {/* Error Message */}
                    {error && (
                        <Typography
                            color="error"
                            variant="body2"
                            sx={{ mt: 2, textAlign: 'center' }}
                        >
                            {error}
                        </Typography>
                    )}
                </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={handleClose} disabled={uploading} sx={{ textTransform: 'none' }}>
                    Cancel
                </Button>
                <Button
                    onClick={handleUpload}
                    variant="contained"
                    disabled={uploading || selectedFiles.length === 0}
                    sx={{ textTransform: 'none', minWidth: 100 }}
                >
                    {uploading ? <CircularProgress size={24} /> : 'Upload'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default UploadDialog;
